<template>
  <div class="container">
    <div class="columns">
      <div class="column is-6"><LsSupportHeader :label="`Message ${brandName}`" /></div>
    </div>
    <div class="columns">
      <div class="column ls-left-column">
        <p>{{ t('forms.describeIssue.header', { brandName }) }}</p>

        <form test-id="form" novalidate @submit.prevent="messageLS">
          <LsSelect
            v-model="reason"
            name="reason"
            :options="reasonOptions"
            :validation="{ required: true }"
            track-id="message_ls_view-reason"
            :label="t('forms.describeIssue.whatIsTheIssue')"
            :placeholder="t('forms.describeIssue.selectReason')"
          />

          <div v-if="!isScheduling">
            <LsInputLabel :label="t('forms.describeIssue.provideMoreInformation')" />
            <LsInput
              v-model="message"
              name="message"
              type="textarea"
              class="message_Ls-input"
              :validation="{ required: true }"
              track-id="message_ls_view-message"
              :placeholder="t('forms.describeIssue.placeholder')"
            />

            <LsButton
              :disabled="support.loadingStatus.isUpdating"
              :loading="support.loadingStatus.isUpdating"
              :text="t('sendMessage').toUpperCase()"
              bg-color="tertiary"
              test-id="send_message"
              track-id="message_ls_view-send_message"
              type="submit"
            />
          </div>

          <div v-if="isScheduling" class="call_support-div" test-id="call_support-div">
            <p>
              <a test-id="call_support-inbox" @click="toInbox">{{
                t(`${schedulingIssue}.contactPro`)
              }}</a>
              {{ t(`${schedulingIssue}.forPreciseInformation`) }}
            </p>
            <p>
              {{ t(`${schedulingIssue}.callUsAt`) }} <a :href="supportNumber">1-866-822-1766</a>
              {{ t(`${schedulingIssue}.hereToHelp`) }}
            </p>

            <a :href="supportNumber">
              <LsButton
                :text="t('callSupport').toUpperCase()"
                bg-color="tertiary"
                test-id="call_support"
                track-id="message_ls_view-call_support"
              />
            </a>
          </div>
        </form>
      </div>
      <div class="column ls-right-column">
        <h3>{{ t('forms.describeIssue.relatedLinksCta') }}</h3>
        <LsNavItems :items="items" test-id="nav_items" />
      </div>
    </div>
  </div>
</template>

<script>
import { lsI18nService, lsModalService } from 'ls-vue-common';
import { LsButton, LsInput, LsSelect, LsInputLabel } from '@/components/common';
import { mapActions, mapState } from 'vuex';
import { SUPPORT__SUBMIT_MESSAGE } from '@/store/types';
import { LsNavItems, LsSupportHeader } from '../components';
import { themedComponentMixin } from '@/mixins';
import MessageReceivedModal from '@/modals/MessageReceivedModal/MessageReceivedModal.vue';

export default {
  name: 'MessageLSView',
  $_veeValidate: {
    validator: 'new',
  },
  components: {
    LsButton,
    LsInput,
    LsNavItems,
    LsSupportHeader,
    LsSelect,
    LsInputLabel,
  },
  mixins: [themedComponentMixin],
  data() {
    return {
      message: '',
      reason: null,
      items: [
        {
          label: 'Skip a mow',
          onClick: () => {
            this.$router.push({ name: 'skipMow' });
          },
          type: 'chevron',
        },
        {
          label: 'Pause service',
          onClick: () => {
            this.$router.push({ name: 'pauseSubscription' });
          },
          type: 'chevron',
        },
        {
          label: 'Contact your crew',
          onClick: () => {
            this.$router.push({ name: 'inbox' });
          },
          type: 'chevron',
        },
        {
          label: 'Order additional services',
          onClick: () => {
            this.$router.push({ name: 'whatOtherServices' });
          },
          type: 'chevron',
        },
        {
          label: 'Change mowing frequency',
          onClick: () => {
            this.$router.push({
              name: 'support',
              query: {
                modal: 'frequencyChange',
              },
            });
          },
          type: 'chevron',
        },
      ],
    };
  },
  computed: {
    ...mapState(['support']),
    schedulingIssue() {
      return 'forms.describeIssue.schedulingIssue';
    },
    supportNumber() {
      return 'tel:1-866-822-1766';
    },
    isScheduling() {
      return this.reason === 'Scheduling';
    },
    reasonOptions() {
      return [
        {
          label: lsI18nService.translate('forms.describeIssue.issues.scheduling'),
          value: 'Scheduling',
        },
        {
          label: lsI18nService.translate('forms.describeIssue.issues.billing'),
          value: 'Billing',
        },
        {
          label: lsI18nService.translate('forms.describeIssue.issues.additionalServices'),
          value: 'Additional services',
        },
        {
          label: lsI18nService.translate('forms.describeIssue.issues.quoteQuestion'),
          value: 'Quote question',
        },
        {
          label: lsI18nService.translate('forms.describeIssue.issues.pauseOrCancel'),
          value: 'Pause or Cancel',
        },
        {
          label: lsI18nService.translate('forms.describeIssue.issues.reportProblem'),
          value: 'Service quality problem',
        },
        {
          label: lsI18nService.translate('forms.describeIssue.issues.getTechHelp'),
          value: 'Technical support',
        },
      ];
    },
  },
  methods: {
    ...mapActions([SUPPORT__SUBMIT_MESSAGE]),
    toInbox() {
      return this.$router.push({ name: 'inbox' });
    },
    async messageLS() {
      const valid = await this.$validator.validateAll();

      if (!valid || this.isScheduling) {
        return;
      }

      this[SUPPORT__SUBMIT_MESSAGE]({
        message: this.message,
        topic: this.reason,
        useToast: false,
        onSuccess: (withSideEffect) => {
          this.$router.push({ name: 'support' });
          if (withSideEffect)
            lsModalService.open({
              parent: this,
              component: MessageReceivedModal,
            });
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.call_support-div {
  margin-top: 13px;
  margin-bottom: 25px;
}

.message_Ls-input {
  margin-bottom: 25px;
}

h3 {
  font-weight: bold;
  font-size: 14px;
  margin-bottom: 15px;
}

p:not(:last-child) {
  margin-bottom: 16px;
}
</style>
